import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

export const VoiceBotContext = createContext(undefined);

export const VoiceBotProvider = ({ children, featureFlags }) => {
  const [isFooterSticky, setIsFooterSticky] = useState(true);
  const [cartState, setCartState] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const addToCartHandlerRef = useRef(null);

  const registerAddToCartHandler = useCallback((handler) => {
    addToCartHandlerRef.current = handler;
  }, []);

  const triggerAddToCart = useCallback(() => {
    if (addToCartHandlerRef.current) {
      addToCartHandlerRef.current();
    }
  }, []);

  const reportError = useCallback((error) => {
    setErrorMessage(error);
  }, []);

  const contextValue = useMemo(
    () => ({
      isFooterSticky,
      setIsFooterSticky,
      cartState,
      setCartState,
      errorMessage,
      reportError,
      registerAddToCartHandler,
      triggerAddToCart,
      featureFlags,
    }),
    [
      isFooterSticky,
      cartState,
      errorMessage,
      reportError,
      registerAddToCartHandler,
      triggerAddToCart,
      featureFlags,
    ]
  );

  return (
    <VoiceBotContext.Provider value={contextValue}>
      {children}
    </VoiceBotContext.Provider>
  );
};

VoiceBotProvider.propTypes = {
  children: PropTypes.node.isRequired,
  featureFlags: PropTypes.object,
};

VoiceBotProvider.defaultProps = {
  featureFlags: {},
};

export const useVoiceBotContext = () => {
  const context = useContext(VoiceBotContext);
  if (context === undefined) {
    throw new Error('useVoiceBotContext must be used within a VoiceBotProvider');
  }
  return context;
};

/**
 * Safe version that returns undefined when outside VoiceBotProvider.
 * Use when the component may render outside the provider (e.g. in tests).
 */
export const useVoiceBotContextSafe = () => useContext(VoiceBotContext);